import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, X, Save, Plane } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const empty = { country: '', price: 0, requirements: '', processing_time: '', image: '' };

const AdminVisas = () => {
  const { adminAuth } = useOutletContext();
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/visas`);
      if (res.ok) setVisas(await res.json());
    } catch { toast.error('Failed to load visas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openCreate = () => { setForm(empty); setEditingId(null); setShowForm(true); };
  const openEdit = (v) => {
    setForm({ country: v.country, price: v.price || 0, requirements: v.requirements || '', processing_time: v.processing_time || '', image: v.image || '' });
    setEditingId(v.id); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(empty); };

  const save = async () => {
    if (!form.country.trim()) { toast.error('Country is required'); return; }
    setSaving(true);
    try {
      const url = editingId ? `${API_URL}/api/admin/visas/${editingId}` : `${API_URL}/api/admin/visas`;
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editingId ? 'Visa updated' : 'Visa added'); closeForm(); load(); }
      else toast.error('Failed to save');
    } catch { toast.error('Connection error'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this visa?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/visas/${id}`, { method: 'DELETE', headers: { Authorization: `Basic ${adminAuth}` } });
      if (res.ok) { toast.success('Deleted'); load(); } else toast.error('Failed to delete');
    } catch { toast.error('Connection error'); }
  };

  const getFullUrl = (u) => (!u ? '' : u.startsWith('http') ? u : `${API_URL}${u}`);

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Visa Services</h1>
          <p className="text-gray-600">Manage visa destinations, approx prices & requirements</p>
        </div>
        {!showForm && (
          <Button onClick={openCreate} data-testid="new-visa-btn" className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" /> Add Visa
          </Button>
        )}
      </div>

      {showForm ? (
        <Card data-testid="visa-form">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{editingId ? 'Edit Visa' : 'New Visa'}</CardTitle>
              <button onClick={closeForm} className="p-2 text-gray-500 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Country / Destination</label>
                <Input data-testid="visa-country-input" value={form.country} onChange={(e) => setField('country', e.target.value)} placeholder="e.g. Vietnam" /></div>
              <div><label className="block text-sm font-medium mb-1">Approx Price (INR)</label>
                <Input type="number" min="0" data-testid="visa-price-input" value={form.price} onChange={(e) => setField('price', parseInt(e.target.value) || 0)} /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Processing Time</label>
              <Input data-testid="visa-time-input" value={form.processing_time} onChange={(e) => setField('processing_time', e.target.value)} placeholder="e.g. 3-5 working days" /></div>
            <div><label className="block text-sm font-medium mb-1">Requirements (one per line)</label>
              <Textarea data-testid="visa-requirements-input" rows={5} value={form.requirements} onChange={(e) => setField('requirements', e.target.value)} placeholder={'Valid passport (6 months)\nPassport photo\nReturn ticket'} /></div>
            <ImageUploader value={form.image} onChange={(url) => setField('image', url)} uploadEndpoint="/api/admin/upload/visa" adminAuth={adminAuth} label="Destination Image" />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={closeForm}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-visa-btn" className="bg-amber-600 hover:bg-amber-700">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Visa'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visas.length === 0 ? (
            <Card className="border-dashed border-2"><CardContent className="p-12 text-center">
              <Plane className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No visas yet</h3>
              <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700"><Plus className="w-4 h-4 mr-2" />Add Your First Visa</Button>
            </CardContent></Card>
          ) : (
            visas.map((v) => (
              <Card key={v.id} data-testid={`admin-visa-${v.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {v.image && <img src={getFullUrl(v.image)} alt={v.country} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{v.country}</h3>
                      {v.price > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">~₹{Number(v.price).toLocaleString('en-IN')}</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{v.processing_time}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(v)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-visa-${v.id}`}><Edit className="w-4 h-4" /></button>
                    <button onClick={() => remove(v.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-visa-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminVisas;
